import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './chip.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { LinkTargetTypes } from 'elements/link/link.js'


initializeIconsByRole({
  'cse-chip.selected': { name: 'check', style: 'light' },
  'cse-chip.dismiss': { name: 'xmark', style: 'light' },
  'cse-chip.dropdown': { name: 'angle-down', style: 'light' },
})

export const chipVariants = ['default', 'dismissable', 'dropdown'] as const
export type chipVariantTypes = typeof chipVariants

@customElement('cse-chip')
export class CSEChip extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  variant: chipVariantTypes[number] = 'default'

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  selected = false

  @property({ type: String })
  url = ''

  @property({ type: String })
  target: LinkTargetTypes[number] = '_self'

  @property({ type: String })
  fit: 'auto' | 'fill' = 'auto'

  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel = ''

  handleIconClick = (event: MouseEvent) => {
    if (this.disabled) return

    event.preventDefault()
    event.stopPropagation()

    this.dispatchEvent(new CSEChipDismissedEvent(this))
  }

  private renderSelectedIcon = () => html`
    <cse-icon class="icon" icon-role="cse-chip.selected"></cse-icon>
  `

  private renderDropdownIcon = () => html`
    <cse-icon class="icon" icon-role="cse-chip.dropdown"></cse-icon>
  `

  private renderDismissButton = () => html`
    <cse-icon-button
      class="icon"
      icon-role="cse-chip.dismiss"
      @click="${this.handleIconClick}"
      aria-label="${t('dismiss_button', { ns: 'cse-chip', default: 'Dismiss button' })}"
      variant="ghost"
      size="small"
    ></cse-icon-button>
  `

  override render() {
    const isLink = this.url !== ''
    const classes = classMap({
      'cse-chip': true,
      'font-body-s': true,
      'selected': this.selected,
      'full-width': this.fit === 'fill',
      'dismissable': this.variant === 'dismissable'
    })

    if (isLink) {
      return html`
        <a
          href="${this.url}"
          class=${classes}
          target=${this.target}
          aria-label="${this.ariaLabel}"
        >
          <slot name="prefix"></slot>
          <slot></slot>
        </a>
      `
    } else {
      return html`
        <button
          class=${classes}
          ?disabled=${this.disabled}
          aria-label="${this.ariaLabel}"
        >
          ${this.selected ? this.renderSelectedIcon() : nothing}
          <slot name="prefix"></slot>
          <slot></slot>
          ${this.variant==='dropdown' ? this.renderDropdownIcon() : nothing}
          ${this.variant==='dismissable' ? this.renderDismissButton() : nothing}
        </button>
      `
    }
  }
}


export class CSEChipDismissedEvent extends Event {
  static type = 'cse-chip-dismissed' as const

  public chip: CSEChip

  constructor(chip: CSEChip) {
    super(CSEChipDismissedEvent.type, { bubbles: true, composed: true })

    this.chip = chip
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-chip': CSEChip
  }
  interface GlobalEventHandlersEventMap {
    [CSEChipDismissedEvent.type]: CSEChipDismissedEvent,
  }
}
